import React, { useState, useEffect, useCallback } from 'react';
import './FreeAgentsPage.css';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { numberToGrade, getAttrColor, getOverallColor } from '../utils/grades';

/* ===== TYPES ===== */
interface TeamFromDB {
  id: string;
  name: string;
  city?: string;
  league_id: string;
  owner_id?: string;
}

interface FreeAgentPlayer {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  career_season: number;
  is_hitter: boolean;
  is_scrub: boolean;

  avatar_url?: string;
  in_free_agency: boolean;
  power: number;
  contact: number;
  speed: number;
  fielding: number;
  arm: number;
  discipline: number;
  endurance: number;
  velocity: number;
  control: number;
  movement: number;
  deception: number;
  user_id?: string;
  league_id?: string;
}

type SortKey = 'overall' | 'name' | 'career_season' | 'position';
const PAGE_SIZE = 30;
const POSITIONS_ALL = ['All', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1` : '';

/* ===== COMPONENT ===== */
interface FreeAgentsPageProps {
  onNavigate?: (page: string) => void;
  userData?: any;
}

const FreeAgentsPage: React.FC<FreeAgentsPageProps> = ({ onNavigate, userData }) => {
  const [ownedTeams, setOwnedTeams] = useState<TeamFromDB[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamFromDB | null>(null);
  const [loading, setLoading] = useState(true);

  const [freeAgents, setFreeAgents] = useState<FreeAgentPlayer[]>([]);
  const [faCount, setFaCount] = useState(0);
  const [faLoading, setFaLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [posFilter, setPosFilter] = useState('All');
  const [sortKey, setSortKey] = useState<SortKey>('overall');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Sign modal
  const [signPlayer, setSignPlayer] = useState<FreeAgentPlayer | null>(null);
  const [waivePlayerId, setWaivePlayerId] = useState('');
  const [teamRoster, setTeamRoster] = useState<any[]>([]);
  const [signing, setSigning] = useState(false);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const totalPages = Math.ceil(faCount / PAGE_SIZE);

  function showToast(type: 'success' | 'error', text: string) {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  }

  /* ===== LOAD TEAMS ===== */
  useEffect(() => { loadTeams(); }, [userData?.id]);

  async function loadTeams() {
    setLoading(true);
    if (!isSupabaseConfigured() || !supabase) { setLoading(false); return; }
    const userId = userData?.id;
    if (userId) {
      const { data: owned } = await supabase.from('teams').select('*').eq('owner_id', userId);
      setOwnedTeams(owned || []);
      if (owned && owned.length > 0 && !selectedTeam) setSelectedTeam(owned[0]);
    }
    setLoading(false);
  }

  /* ===== LOAD FREE AGENTS ===== */
  useEffect(() => {
    if (selectedTeam) loadFreeAgents();
  }, [selectedTeam, page, sortKey, sortDir, searchQuery, posFilter]);

  async function loadFreeAgents() {
    if (!isSupabaseConfigured() || !supabase) return;
    setFaLoading(true);
    try {
      // Show: (1) players in this league with no team, OR (2) players globally in free agency
      let query = supabase
        .from('players')
        .select('*', { count: 'exact' })
        .is('team_id', null)
        .eq('retired', false)
        .eq('is_scrub', false);

      if (selectedTeam) {
        query = query.or(`league_id.eq.${selectedTeam.league_id},in_free_agency.eq.true`);
      } else {
        query = query.eq('in_free_agency', true);
      }

      if (posFilter !== 'All') query = query.eq('position', posFilter);
      if (searchQuery.trim()) {
        const q = searchQuery.trim();
        query = query.or(`first_name.ilike.%${q}%,last_name.ilike.%${q}%`);
      }
      if (sortKey === 'name') query = query.order('last_name', { ascending: sortDir === 'asc' });
      else if (sortKey === 'overall') query = query.order('overall', { ascending: false });
      else if (sortKey === 'career_season') query = query.order('career_season', { ascending: sortDir === 'asc' });
      else if (sortKey === 'position') query = query.order('position', { ascending: true });

      const from = page * PAGE_SIZE;
      query = query.range(from, from + PAGE_SIZE - 1);

      const { data, count, error } = await query;
      if (!error) { setFreeAgents(data || []); setFaCount(count || 0); }
    } catch (e) { console.error('loadFreeAgents', e); }
    setFaLoading(false);
  }

  /* ===== LOAD ROSTER FOR WAIVE ===== */
  async function loadRoster(teamId: string) {
    if (!supabase) return;
    const { data } = await supabase.from('players')
      .select('id, first_name, last_name, position, overall, is_hitter, is_scrub')
      .eq('team_id', teamId).eq('retired', false)
      .order('overall', { ascending: true }); // lowest OVR first for easy waiving
    setTeamRoster(data || []);
  }

  /* ===== OPEN SIGN MODAL ===== */
  async function openSignModal(player: FreeAgentPlayer) {
    setSignPlayer(player);
    setWaivePlayerId('');
    if (selectedTeam) await loadRoster(selectedTeam.id);
  }

  /* ===== SIGN PLAYER ===== */
  async function handleSign() {
    if (!signPlayer || !selectedTeam || !supabase) return;
    setSigning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/sign-player`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          action: 'sign',
          playerId: signPlayer.id,
          teamId: selectedTeam.id,
          waivePlayerId: waivePlayerId || undefined,
        }),
      });
      const data = await res.json();
      if (data.error) {
        showToast('error', data.error);
      } else {
        showToast('success', `✅ ${data.player?.name} signed to ${data.team?.name}!`);
        setSignPlayer(null);
        loadFreeAgents();
      }
    } catch (e: any) {
      showToast('error', e.message);
    }
    setSigning(false);
  }

  /* ===== RENDER ATTR BAR ===== */
  function renderAttrBar(label: string, val: number) {
    return (
      <div key={label} style={{ marginBottom: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: getAttrColor(val) }}>{numberToGrade(val)}</span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: '#21262d' }}>
          <div style={{ height: 4, borderRadius: 2, width: `${val}%`, background: getAttrColor(val) }} />
        </div>
      </div>
    );
  }

  /* ===== RENDER SIGN MODAL ===== */
  function renderSignModal() {
    if (!signPlayer) return null;
    const isHitter = signPlayer.is_hitter;
    const rosterFull = teamRoster.length >= 25;
    const attrs = isHitter
      ? [
          { label: 'Power', value: signPlayer.power },
          { label: 'Contact', value: signPlayer.contact },
          { label: 'Speed', value: signPlayer.speed },
          { label: 'Fielding', value: signPlayer.fielding },
          { label: 'Arm', value: signPlayer.arm },
          { label: 'Discipline', value: signPlayer.discipline },
        ]
      : [
          { label: 'Velocity', value: signPlayer.velocity },
          { label: 'Control', value: signPlayer.control },
          { label: 'Movement', value: signPlayer.movement },
          { label: 'Deception', value: signPlayer.deception },
          { label: 'Endurance', value: signPlayer.endurance },
          { label: 'Speed', value: signPlayer.speed },
        ];

    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#f0ede6' }}>{signPlayer.first_name} {signPlayer.last_name}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <span style={{ background: '#21262d', color: '#9ca3af', borderRadius: 6, padding: '2px 8px', fontSize: 12 }}>{signPlayer.position}</span>
                <span style={{ background: '#21262d', color: getOverallColor(signPlayer.overall), borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>{numberToGrade(signPlayer.overall)}</span>

              </div>
            </div>
            <button onClick={() => setSignPlayer(null)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>

          {/* Signing to */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: '#9ca3af', display: 'block', marginBottom: 6 }}>Sign to team</label>
            {ownedTeams.length > 1 ? (
              <select
                style={{ width: '100%', padding: '8px 12px', background: '#21262d', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}
                value={selectedTeam?.id || ''}
                onChange={async e => {
                  const t = ownedTeams.find(t => t.id === e.target.value) || null;
                  setSelectedTeam(t);
                  setWaivePlayerId('');
                  if (t) await loadRoster(t.id);
                }}
              >
                {ownedTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            ) : (
              <div style={{ padding: '8px 12px', background: '#21262d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}>
                {selectedTeam?.name || '—'}
              </div>
            )}
          </div>

          {/* Roster full warning + waive picker */}
          {rosterFull && (
            <div style={{ marginBottom: 16, padding: '12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginBottom: 8 }}>⚠️ Roster full — waive a player to make room</div>
              <select
                style={{ width: '100%', padding: '8px 12px', background: '#21262d', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 13 }}
                value={waivePlayerId}
                onChange={e => setWaivePlayerId(e.target.value)}
              >
                <option value=''>— Select player to waive —</option>
                {teamRoster.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.first_name} {p.last_name} | {p.position} | {numberToGrade(p.overall)}{p.is_scrub ? ' (scrub)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Attributes */}
          <div style={{ marginBottom: 20 }}>
            {attrs.map(a => renderAttrBar(a.label, a.value))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setSignPlayer(null)} style={{ flex: 1, padding: '10px', background: '#21262d', border: '1px solid #30363d', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 14 }}>
              Cancel
            </button>
            <button
              onClick={handleSign}
              disabled={signing || (rosterFull && !waivePlayerId)}
              style={{ flex: 2, padding: '10px', background: (signing || (rosterFull && !waivePlayerId)) ? '#21262d' : '#22c55e', border: 'none', borderRadius: 8, color: (signing || (rosterFull && !waivePlayerId)) ? '#6b7280' : '#000', cursor: (signing || (rosterFull && !waivePlayerId)) ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 700 }}
            >
              {signing ? 'Signing...' : '✅ Sign Player'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ===== MAIN RENDER ===== */
  if (loading) return <div className="fa-page"><div className="fa-loading">Loading...</div></div>;
  if (!supabase || !isSupabaseConfigured()) return <div className="fa-page"><div className="fa-loading">Not configured</div></div>;

  return (
    <div className="fa-page">
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000, padding: '12px 20px', borderRadius: 10, background: toast.type === 'success' ? '#166534' : '#7f1d1d', color: '#fff', fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }}>
          {toast.text}
        </div>
      )}

      {renderSignModal()}

      <div className="fa-header">
        <h1 className="fa-title">Free Agency</h1>
        <p className="fa-subtitle">Sign available players to your team — no contracts, no salary. Just talent.</p>
      </div>

      {/* No teams owned */}
      {ownedTeams.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>You don't own any teams yet.</div>
      )}

      {ownedTeams.length > 0 && (
        <>
          {/* Team selector (if multiple) */}
          {ownedTeams.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <select
                style={{ padding: '8px 12px', background: '#161b22', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}
                value={selectedTeam?.id || ''}
                onChange={e => {
                  const t = ownedTeams.find(t => t.id === e.target.value) || null;
                  setSelectedTeam(t);
                  setPage(0);
                }}
              >
                {ownedTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}

          {/* Filters */}
          <div className="fa-filters" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <input
              className="fa-search"
              placeholder="Search player..."
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(0); }}
              style={{ flex: 1, minWidth: 160, padding: '8px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}
            />
            <select
              style={{ padding: '8px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}
              value={posFilter}
              onChange={e => { setPosFilter(e.target.value); setPage(0); }}
            >
              {POSITIONS_ALL.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              style={{ padding: '8px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, color: '#f0ede6', fontSize: 14 }}
              value={`${sortKey}-${sortDir}`}
              onChange={e => {
                const [k, d] = e.target.value.split('-');
                setSortKey(k as SortKey);
                setSortDir(d as 'asc' | 'desc');
                setPage(0);
              }}
            >
              <option value="overall-desc">OVR ↓</option>
              <option value="overall-asc">OVR ↑</option>
              <option value="name-asc">Name A-Z</option>
              <option value="career_season-asc">Age ↑</option>
              <option value="career_season-desc">Age ↓</option>
              <option value="position-asc">Position</option>
            </select>
          </div>

          {/* Player count */}
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            {faLoading ? 'Loading...' : `${faCount} available player${faCount !== 1 ? 's' : ''}`}
          </div>

          {/* Player list */}
          {!faLoading && freeAgents.length === 0 && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>No free agents available.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {freeAgents.map(player => (
              <div key={player.id} style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                {/* Avatar */}
                {player.avatar_url ? (
                  <img src={player.avatar_url} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 44, height: 44, borderRadius: 8, background: '#21262d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {player.position === 'P' ? '⚾' : '🏏'}
                  </div>
                )}

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#f0ede6', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {player.first_name} {player.last_name}
                    {player.in_free_agency && <span style={{ marginLeft: 6, fontSize: 10, background: '#1d4ed8', color: '#93c5fd', borderRadius: 4, padding: '1px 5px' }}>GLOBAL FA</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{player.position}</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>·</span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>Season {player.career_season}</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>·</span>

                  </div>
                </div>

                {/* OVR */}
                <div style={{ fontSize: 18, fontWeight: 800, color: getOverallColor(player.overall), flexShrink: 0 }}>{numberToGrade(player.overall)}</div>

                {/* Sign button */}
                <button
                  onClick={() => openSignModal(player)}
                  style={{ flexShrink: 0, padding: '7px 14px', background: '#22c55e', border: 'none', borderRadius: 8, color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  Sign
                </button>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: '6px 14px', background: '#21262d', border: 'none', borderRadius: 6, color: page === 0 ? '#4b5563' : '#f0ede6', cursor: page === 0 ? 'not-allowed' : 'pointer' }}>←</button>
              <span style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 13 }}>{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: '6px 14px', background: '#21262d', border: 'none', borderRadius: 6, color: page >= totalPages - 1 ? '#4b5563' : '#f0ede6', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer' }}>→</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FreeAgentsPage;
